import { getLang } from "@ds/base";
import type { MessageKind } from "@ds/components";
import { TranslationContext, Interpolate } from "@ds/react-utils";
import { Button, Checkbox, RadioButton, RadioButtonGroup, FlexRow, TextArea, Heading, ToastMessage, Link } from "@ds/ui";
import React, { FunctionComponent } from "react";
import Errors from "../common/Errors";

const USER_PHONE_ELT_ID = 'userPhone';

function FormUnit({ children }: { children: React.ReactNode }) {
    return (
        <div style={{ marginBlock: '24px' }}>
            {children}
        </div>
    )
}

const getPrivacyPolicyLink = () => {
    const privacyPolicyFooterElement = document.querySelector("a[data-qa='footer-link-PRIVACY']");
    return privacyPolicyFooterElement ? privacyPolicyFooterElement.getAttribute("href") : "https://www.docusign.com/company/privacy-policy";
}

const getTermsAndConditionsLink = () => {
    const termsAndConditionsFooterElement = document.querySelector("a[data-qa='footer-link-TERMS']");
    return termsAndConditionsFooterElement ? termsAndConditionsFooterElement.getAttribute("href") : "https://www.docusign.com/company/terms-and-conditions/web";
}

interface IReasons {
    notifications: boolean;
    spam: boolean;
    illegalFraud: boolean;
}
interface IToast {
    kind: MessageKind;
    message: string;
    show: boolean;
    allowClose: boolean;
}
const ReportAbuse: FunctionComponent<{}> = () => {
    const translate = React.useContext(TranslationContext);
    const defaultReasons = { notifications: false, spam: false, illegalFraud: false };
    const [reasons, setReasons] = React.useState<IReasons>(defaultReasons);
    const [comment, setComment] = React.useState("");
    const [contactMe, setContactMe] = React.useState(false);
    const [shareWithSender, setShareWithSender] = React.useState(false);
    const [consentToShareWithFraudTeam, setConsentToShareWithFraudTeam] = React.useState(false);
    const [toast, setToast] = React.useState<IToast>({ kind: "success", message: translate("Form submitted"), show: false, allowClose: true });
    const [hasError, setHasError] = React.useState<boolean>(false);
    const [submitDisabled, setSubmitDisabled] = React.useState(false);
    const [submitRedirect, setRedirect] = React.useState(false);
    const [reasonsError, setReasonsError] = React.useState("");
    const [showThankYou, setShowThankYou] = React.useState(false);
    const [showRedirect, setShowRedirect] = React.useState(false);
    const [submissionReady, setSubmissionReady] = React.useState(false);

    const supportLangs = ['en', 'fr', 'br', 'jp', 'de'];
    const howItWorksLangs = {
        'en-AU': "https://www.docusign.com/how-it-works/legality/global",
        'en-CA': 'https://www.docusign.com/how-it-works/legality/global/canada',
        'fr': "https://www.docusign.com/how-it-works/legality/global",
        'en-gb': 'https://www.docusign.com/how-it-works/legality/global/the-united-kingdom',
        'en': 'https://www.docusign.com/how-it-works/legality/global',
        'nl': 'https://www.docusign.com/how-it-works/legality/global/netherlands',
        'ja': 'https://www.docusign.com/how-it-works/legality/global/japan',
        'de': 'https://docusign.de/wie-es-funktioniert/rechtsg%C3%BCltigkeit',
        'pt': 'https://docusign.com.br/legislacao',
        'es': 'https://www.docusign.com/how-it-works/legality/global/spain',
        'es-MX': 'https://www.docusign.com/how-it-works/legality/global/mexico'
    };
    const metadataType = getUrlParameter('type') || undefined;
    const lang = window["lang"] ?? "en";
    const iSightURL = window["iSightURL"] ?? "https://www.docusign.com/404";
    const enableConsentToShareWithFraudTeamCheckbox: boolean = window["enableConsentToShareWithFraudCheckbox"];
    const enableNonEnvelopeReportAbusePage = window["enableNonEnvelopeReportAbusePage"];
    const enableReportAbuseDisplayPhoneNumber = window["enableReportAbuseDisplayPhoneNumber"];

    const isEnvelopeReportAbuseLink = () => metadataType === undefined || metadataType.toLowerCase() == "envelope";
    const shouldHideEnvelopeSpecificHtml = () => enableNonEnvelopeReportAbusePage && !isEnvelopeReportAbuseLink();

    const userPhone = document.getElementById(USER_PHONE_ELT_ID)?.getAttribute('value');

    const getConsentToShareWithFraudTeamLabel = () => {
        const markup = translate("I am the intended recipient and agree to share this content with Docusign. Docusign will retain this content solely for fraud prevention purposes and in accordance with the <PrivacyPolicyLink>privacy policy</PrivacyPolicyLink> and <TermsAndConditionsLink>Terms and Conditions</TermsAndConditionsLink>.");
        return <div>
            <Interpolate text={markup}
                components={{
                    TermsAndConditionsLink: (children) => <Link text={children} href={getTermsAndConditionsLink()} />,
                    PrivacyPolicyLink: (children) => <Link text={children} href={getPrivacyPolicyLink()} />
                }} />
        </div>
    }

    const getTermsAndConditions = () => {
        const termsAndConditionsLinkText = translate("Terms and Conditions.");
        const termsAndConditionsLink = getTermsAndConditionsLink();
        const start = `<a href='${termsAndConditionsLink}'>`;
        const end = "</a>";
        const key = "{{termsAndConditionsLink}}";
        const text = translate("Docusign investigates and takes action against senders who violate our {{start}}{{termsAndConditionsLink}}{{end}}");
        return <div style={{ marginTop: "40px", textAlign: "center" }} dangerouslySetInnerHTML={createDangerousMarkup(text, start, key, termsAndConditionsLinkText, end)}></div>;
    }

    const getPrivacyPolicy = () => {
        const privacyPolicyLinkText = translate("Privacy Policy");
        const privacyPolicyLink = getPrivacyPolicyLink();
        const start = `<a href='${privacyPolicyLink}'>`;
        const end = "</a>";
        const key = "{{privacyPolicyLink}}";
        const text = translate("Docusign will only use your contact email if we have additional questions about the abuse report.  Your email will not be used for any marketing purposes.  For more information, see our {{start}}{{privacyPolicyLink}}{{end}}");
        return <div style={{ marginTop: "15px", textAlign: "center" }} dangerouslySetInnerHTML={createDangerousMarkup(text, start, key, privacyPolicyLinkText, end)}></div>;
    }

    const howDoESignWork = () => {
        const text = translate("The sender can specify and order any number of signers.  Recipients electronically sign documents from virtually any device.  {{start}}{{readMore}}{{end}}")
        const link = getReadMoreLink();
        const start = `<a target='_blank' href='${link}' style={{ color: 'white' }}>`;
        const readMore = translate("Read more");
        const end = "</a>";
        const key = "{{readMore}}";
        return <div style={{ position: "relative", top: "-10px" }} dangerouslySetInnerHTML={createDangerousMarkup(text, start, key, readMore, end)}></div>;
    }

    const getReadMoreLink = () => {
        const lang = getLang();
        let url = howItWorksLangs[lang];

        return url || howItWorksLangs['en']; //default to 'en'
    }

    const createDangerousMarkup = (text, start, key, markupText, end) => {
        return { __html: text.replace('{{start}}', start).replace(key, markupText).replace('{{end}}', end) };
    }


    const checkError = (clientError?: string) => {
        let errorMessage = null;
        let isClientError = false;
        if (clientError && clientError.length) {
            errorMessage = getErrorMessage(clientError);
            isClientError = true;
        } else {
            const errorCodeElement = document.getElementById("errorCode");
            if (errorCodeElement) {
                const errorCode = errorCodeElement.getAttribute("value");
                if (errorCode && errorCode.length > 0 && !hasError) {
                    errorMessage = getErrorMessage(errorCode);
                }
            }
        }

        if (errorMessage) {
            setToast({ kind: "danger", message: errorMessage, show: true, allowClose: isClientError });
            if (!isClientError) {
                setHasError(true);
            }
        }
    }

    const getErrorMessage = (errorCode) => {
        let errorMessage = Errors(translate).DEFAULT;

        if (errorCode && Object.keys(Errors(translate)).indexOf(errorCode) > -1) {
            errorMessage = Errors(translate)[errorCode];
        }

        return errorMessage;
    };

    const onChangeReasons = (event: any) => {
        const name = event.currentTarget["dataset"] && event.currentTarget["dataset"].name;
        if (name) {
            const newValue = { ...defaultReasons };
            newValue[name] = event.currentTarget.checked;
            setRedirect(newValue.illegalFraud);
            setReasons(newValue);

            clearError();
        }
    };

    const onChangeComment = (event: any) => {
        setComment(event.currentTarget.value);
    }

    const onChangeContactMe = (event: any) => {
        setContactMe(event.currentTarget.checked);
    }

    const onChangeShareWithSender = (event: any) => {
        setShareWithSender(event.currentTarget.checked);
    }

    const onChangeConsentToShareWithFraudTeam = (event: any) => {
        setConsentToShareWithFraudTeam(event.currentTarget.checked);
    }

    const failedApproval = () => {
        checkError(Errors(translate).DEFAULT);
        setSubmitDisabled(false);
    }

    const waitForSubmissionReady = async () => {
        if (submissionReady) {
            return;
        }

        window.addEventListener("dsspSubmissionReady", () => {
            setSubmissionReady(true);
        });
    }

    const dsspSubmit = (event: any) => {
        const isValid = validate();
        if (!isValid) {
            return;
        }

        const dssp = window["dssp"];
        if (dssp && typeof (dssp["submit"]) === "function") {
            dssp.submit(setSubmitDisabled.bind(this, true), failedApproval, event)
                .then((token: string) => {
                    onSubmit(token);
                }, (error) => {
                    checkError(error);
                }).finally(() => { setSubmitDisabled(false) });
        } else {
            onSubmit();
        }
    }

    const onSubmit = async (token: string = null) => {
        const isValid = validate();
        if (!isValid) {
            return;
        }

        setSubmitDisabled(true);

        const e = getUrlParameter('e');
        const requestBody = Object.assign(
            Object.create(null),
            reasons,
            {
                comment,
                contactMe,
                shareWithSender,
                consentToShareWithFraudTeam,
                token,
                metadata: e,
                metadataType
            });

        try {
            let response = await fetch("/api/2.0/report-abuse", {
                method: "post",
                headers: { "Accept": "application/json", "Content-Type": "application/json" },
                body: JSON.stringify(requestBody)
            });
            if (response.status === 200) {
                if (submitRedirect && window["iSightEnabled"]) {
                    let json = await response.json()
                    let refId = json.traceToken ?? "00000000-0000-0000-0000-000000000000";
                    setShowRedirect(true);

                    if (window["enableLangParamISightRedirect"]) {
                        window.location.assign(`${iSightURL}&lang=${lang}&refID=${refId}`);
                    } else {
                        window.location.assign(`${iSightURL}&refID=${refId}`);
                    }
                } else {
                    setShowThankYou(true);
                }
            } else {
                throw (Errors(translate).DEFAULT);
            }
        } catch (error) {
            checkError(error);
            Promise.reject(error);
        } finally {
            setSubmitDisabled(false);
        }
    }

    const closeToast = () => {
        setToast(Object.assign({ ...toast }, { show: false }));
    }

    const validate = () => {
        const isValid = !(!reasons.notifications && !reasons.spam && !reasons.illegalFraud);
        if (!isValid) {
            setReasonsError(translate("Select a reason to submit a report"));
        } else {
            clearError();
        }

        return isValid;
    }

    const clearError = () => {
        reasonsError && reasonsError.length > 0 && setReasonsError("");
    }

    function getUrlParameter(name) {
        name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
        var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
        var results = regex.exec(location.search);
        return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    };

    const getUserEmail = () => {
        const userEmailElement = document.getElementById("userEmail");
        if (userEmailElement) {
            return userEmailElement.getAttribute("value");
        }

        return "";
    }

    const getSupportedLang = () => {
        let lang = getLang();

        if (!supportLangs.includes(lang)) {
            lang = 'en';
        }

        return lang;
    }

    const getDeclineToSignLink = () => {
        const lang = getSupportedLang();

        return `https://support.docusign.com/${lang}/guides/Declining-to-sign-DocuSign-Signer-Guide`;
    }

    const getManageEmailNotificationsLink = () => {
        const lang = getSupportedLang();

        return `https://support.docusign.com/${lang}/articles/How-do-I-manage-my-email-notifications`;
    }

    checkError();
    waitForSubmissionReady();

    const userEmail = getUserEmail() || "";
    const mayContactTextWithEmail = translate("Docusign may contact me at the above email address to request additional information.");
    const mayContactTextWithoutEmail = translate("Docusign may contact me to request additional information.");
    const mayContactText = userEmail.length > 0 ? mayContactTextWithEmail : mayContactTextWithoutEmail;
    const shareWithSenderText = translate("Docusign may share your report back to the sender. We will not share your comment.");

    const infoPanel = (
        <div style={{ maxWidth: "350px", color: "white", paddingTop: "10px" }}>
            <h2>{translate("Stop Receiving Notifications")}</h2>
            <ul style={{ position: "relative", top: "-10px", marginBottom: "10px" }}>
                <li>
                    <a target="_blank" href={getDeclineToSignLink()} style={{ color: "white" }}>
                        <h3>{translate("Declining to sign")}</h3>
                    </a>
                </li>
                <li style={{ position: "relative", top: "-10px" }}>
                    <a target="_blank" href={getManageEmailNotificationsLink()} style={{ color: "white" }}>
                        <h3>{translate("Managing notifications")}</h3>
                    </a>
                </li>
            </ul>

            <h2>{translate("More About Documents")}</h2>
            <h3>{translate("Who do I contact with questions?")}</h3>
            <div style={{ position: "relative", top: "-10px" }}>
                {translate("Reply to the notification email to contact the sender")}
            </div>

            <h3>{translate("Who sent this to me?")}</h3>
            <div style={{ position: "relative", top: "-10px" }}>
                {translate("The email notification contains the name and email address of the document sender.")}
            </div>

            <h3>{translate("How do electronic signatures work?")}</h3>
            {howDoESignWork()}

        </div>
    );

    return (
        <>
            {showRedirect && window["iSightEnabled"] &&
                <div style={{ color: "white", textAlign: "center" }}>
                    <h2>{translate("Thank you for helping Docusign identify fraud on our platform.")}</h2>
                    <div>{translate("You’re being redirected to our secure portal to report additional details needed for Docusign to investigate and take appropriate actions.")}</div>
                </div>}
            {showThankYou &&
                <div style={{ color: "white", textAlign: "center" }}>
                    <h2>{translate("Thank You!")}</h2>
                    <div>{translate("Your feedback is important to help us protect our customers.")}</div>
                </div>}
            {!showThankYou && !hasError && !showRedirect &&
                <FlexRow gap={24}>
                    {!shouldHideEnvelopeSpecificHtml() && infoPanel}
                    <div style={{ maxWidth: "350px", padding: "10px 20px", background: "white", height: "fit-content" }}>
                        <h2>{translate("Report Abuse")}</h2>
                        <FormUnit>
                            <RadioButtonGroup
                                error={reasonsError}
                                legend={translate("Reason")}>
                                <RadioButton
                                    data-name="notifications"
                                    label={translate("I know the sender and want them to stop contacting me.")}
                                    checked={reasons.notifications}
                                    onChange={onChangeReasons}
                                />

                                <RadioButton
                                    data-name="spam"
                                    label={translate("I don't know the sender and think this is spam.")}
                                    checked={reasons.spam}
                                    onChange={onChangeReasons}
                                />

                                <RadioButton
                                    data-name="illegalFraud"
                                    label={translate("I believe this is fraudulent or contains illegal content.")}
                                    checked={reasons.illegalFraud}
                                    onChange={onChangeReasons}
                                />

                            </RadioButtonGroup>
                        </FormUnit>
                        <FormUnit>
                            <TextArea
                                label={translate("Comment")}
                                placeholder={translate("Do not include personal information or share the signing link.")}
                                onChange={onChangeComment}
                                characterCount={true}
                                maxLength={window["commentsMaxLength"] ?? 150}
                                value={comment}
                            />
                        </FormUnit>

                        {userEmail.length > 0 &&
                            <FormUnit>
                                <Heading level="6" text={translate("Your Contact Email:")} />
                                <p>{userEmail}</p>
                            </FormUnit>}

                        {enableReportAbuseDisplayPhoneNumber && userPhone &&
                            <FormUnit>
                                <Heading level="6" text={translate("Your Contact Phone:")} />
                                <p>{userPhone}</p>
                            </FormUnit>}

                        {/* Consent to share w/ Fraud team checkbox will replace consent to contact checkbox */}
                        {!enableConsentToShareWithFraudTeamCheckbox &&
                            <FormUnit>
                                <Checkbox
                                    data-name="ContactMe"
                                    label={mayContactText}
                                    onChange={onChangeContactMe}
                                    checked={contactMe}
                                />
                            </FormUnit>
                        }
                        {enableConsentToShareWithFraudTeamCheckbox &&
                            <FormUnit>
                                <Checkbox
                                    data-name="ConsentToShareWithFraudTeam"
                                    label={getConsentToShareWithFraudTeamLabel()}
                                    onChange={onChangeConsentToShareWithFraudTeam}
                                    checked={consentToShareWithFraudTeam}
                                />
                            </FormUnit>
                        }
                        <FormUnit>
                            <Checkbox
                                data-name="ShareWithSender"
                                label={shareWithSenderText}
                                onChange={onChangeShareWithSender}
                                checked={shareWithSender}
                            />
                        </FormUnit>
                        <div className="sp-container" style={{ marginBottom: "10px" }}></div>
                        {!submissionReady
                            ? <Button
                                disabled={true}
                                kind="primary"
                                text={"Loading..."}
                                fullWidth={true} />
                            : <Button
                                disabled={submitDisabled}
                                onClick={dsspSubmit}
                                kind="primary"
                                text={submitRedirect && window["iSightEnabled"] ? translate("Continue") : translate("Send abuse report")}
                                fullWidth={true} />}
                        {getTermsAndConditions()}
                        {contactMe && getPrivacyPolicy()}
                    </div>
                </FlexRow>}
            <ToastMessage
                kind={toast.kind}
                closeButton={toast.allowClose ? <ToastMessage.Close onClick={closeToast} /> : null}
                visible={toast.show}>
                {toast.message}
            </ToastMessage>
        </>
    );
}

export default ReportAbuse
